// ─────────────────────────────────────────────────────────────────────────────
// SHAP UI helpers
// ─────────────────────────────────────────────────────────────────────────────

import {
    FaClock,
    FaCircleQuestion,
    FaHeartPulse,
    FaMoon,
    FaPersonWalking,
    FaWaveSquare,
} from "react-icons/fa6"
import { IconType } from "react-icons"
import { toShapGermanLabel } from "pokal_api"
import type {
    VariantKind,
    RankedSignalDto,
    RankedVariantDto,
    SignalDirection,
} from "pokal_api"

export { toShapGermanLabel as featureLabel }

// ─── Types ───────────────────────────────────────────────────────────────────

// Visual classification of a statement; drives text colour in card rows.
export type StatementType = "positive" | "negative" | "neutral"

export type LayerStatement = {
    text: string
    type: StatementType
}

// What patientStatementsForSignal returns:
//   * rawValuesLine: single line with per-variant raw values + an inline date
//     range bracket describing the lookback window of those values.
//   * contributionPrefix: optional one-line prefix that names the temporal
//     scope of the contribution lines below (e.g., "im Mittel der letzten 5
//     Vorhersagen:").
//   * contributionLines: combined when avg/std agree, split when they
//     disagree.
export type PatientStatement = {
    rawValuesLine: string
    contributionPrefix?: string
    contributionLines: LayerStatement[]
}

// ─── Feature icons ────────────────────────────────────────────────────────────

// react-icons components keyed by feature group
export const FEATURE_ICONS: Record<string, IconType> = {
    sleep_duration: FaMoon,
    sleep_latency: FaClock,
    sleep_heart_rate: FaHeartPulse,
    sleep_hrv: FaWaveSquare,
    steps: FaPersonWalking,
}

// Returns the react-icon component for a feature group; falls back to a
// question mark icon for unknown groups (e.g. a model trained with a new
// feature that the frontend hasn't been updated to label yet).
export function featureIcon(groupKey: string): IconType {
    return FEATURE_ICONS[groupKey] ?? FaCircleQuestion
}

// Short single-character badge used in the Technische Details modal to
// distinguish the two variants of each clinical signal.  Ø = arithmetic
// mean, σ = standard deviation (statistician shorthand).  Empty string for
// "other" so the badge column collapses cleanly.
export function variantBadge(kind: VariantKind): "Ø" | "σ" | "" {
    if (kind === "avg") return "Ø"
    if (kind === "std") return "σ"
    return ""
}

// ─── Enum → presentation mapping ─────────────────────────────────────────────

// Map the API's SignalDirection enum to the frontend's StatementType.
// `increases` (raises the PHQ-8 prediction) is colour-coded as a *risk* (red);
// `decreases` (lowers the prediction) as *protective* (green); `neutral` stays
// gray.
function signalDirectionToStatementType(direction: SignalDirection): StatementType {
    if (direction === "increases") return "positive"
    if (direction === "decreases") return "negative"
    return "neutral"
}

// Color class for a StatementType
export function statementColorClass(type: StatementType): string {
    if (type === "negative") return "text-green-700"
    if (type === "positive") return "text-red-700"
    return "text-gray-400 italic"
}

// Direction arrow + colour class for a signal's row header
export type DirectionMark = {
    symbol: "▲" | "▼" | "—"
    colorClass: string
}

export function directionMark(signal: RankedSignalDto): DirectionMark {
    if (signal.direction === "increases") {
        return { symbol: "▲", colorClass: "text-red-600" }
    }
    if (signal.direction === "decreases") {
        return { symbol: "▼", colorClass: "text-green-700" }
    }
    return { symbol: "—", colorClass: "text-gray-400" }
}

// ─── Raw value formatting per clinical signal ────────────────────────────────
// Used by the Einflussfaktoren card to display this patient's current values
// (mean and variability) with the right units.

const FEATURE_UNIT_FORMATTERS: Record<string, (value: number) => string> = {
    sleep_duration: (v) => `${(v / 3600).toFixed(1)} h`,
    sleep_latency: (v) => `${Math.round(v / 60)} min`,
    sleep_heart_rate: (v) => `${Math.round(v)} bpm`,
    sleep_hrv: (v) => `${Math.round(v)} ms`,
    steps: (v) => Math.round(v).toLocaleString("de-DE"),
}

export function formatFeatureValue(
    groupKey: string,
    value: number | null | undefined
): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return "–"
    const fmt = FEATURE_UNIT_FORMATTERS[groupKey]
    return fmt ? fmt(value) : value.toFixed(1)
}

// ─── Plain-language statement helpers ────────────────

// "Allgemein" layer — describes how this signal relates to PHQ-8 across the
// training population. Rules:
//   * If no variant has a meaningful pearson_r (generalDirection ≠ "neutral"),
//     emit a single neutral fallback line.
//   * If `generalAgreement` is `"agree"` (or `"single"`), emit one combined
//     line.  Add the "Mittelwert und Schwankung wirken gleich" note when
//     *every* variant in the group is meaningful and there are at least two
//     of them
//   * If `generalAgreement` is `"disagree"`, emit one line per meaningful
//     variant so the conflict is visible.
export function generalStatementsForSignal(signal: RankedSignalDto): LayerStatement[] {
    const meaningful = signal.variants.filter((v) => v.generalDirection !== "neutral")

    if (meaningful.length === 0) {
        return [
            {
                text: "Allgemein kein klarer Zusammenhang aus den Trainingsdaten",
                type: "neutral",
            },
        ]
    }

    // generalAgreement === "agree" OR "single" → one combined line.  We use
    // the direction of any meaningful variant (they all agree by definition
    // of `"agree"`; for `"single"` there's only one to pick from).
    if (signal.generalAgreement === "agree" || signal.generalAgreement === "single") {
        const first = meaningful[0]
        const direction = first.generalDirection === "decreases" ? "niedrigerem" : "höherem"
        // Add the parenthetical only when *all* variants are meaningful AND we
        // actually have more than one — matches the old `allBothPresent` rule.
        const allBothPresent =
            meaningful.length === signal.variants.length && signal.variants.length > 1
        const note = allBothPresent ? " (Mittelwert und Schwankung wirken gleich)" : ""
        return [
            {
                text: `Höhere Werte hängen mit ${direction} PHQ-8-Wert zusammen${note}`,
                type: signalDirectionToStatementType(first.generalDirection),
            },
        ]
    }

    // generalAgreement === "disagree" → split into one line per meaningful variant
    return meaningful.map((v) => {
        const subject = v.kind === "std" ? "Stärkere Schwankung" : "Höherer Mittelwert"
        const direction = v.generalDirection === "decreases" ? "niedrigerem" : "höherem"
        return {
            text: `${subject}: hängt mit ${direction} PHQ-8-Wert zusammen`,
            type: signalDirectionToStatementType(v.generalDirection),
        }
    })
}

// "Patient" layer — describes what's happening for THIS patient right now.
// Always shows raw values (mean and variability with units) plus an inline
// date range describing the lookback window of those values (because the mean and variability are shown for one lookback window, but there are multiple with overlapping date ranges when having multiple prediction instances - hope this was not confusing now); then a
// contribution prefix naming the averaging scope, followed by one or more
// contribution lines. Rules:
//   * Raw values line: every variant's currentValue, separated by "·".  An
//     inline ` (vom DD.MM. bis DD.MM.)` suffix is appended when both the
//     instance date and `signal.lookbackDays` are known.
//   * Contribution prefix: `im Mittel der letzten N Vorhersage(n):` when
//     `instanceCount > 0`.  Sits once above the contribution lines so the
//     temporal scope (averaging across N source dates) is explicit without
//     repeating in every line.
//   * Contribution lines:
//     - All-neutral: single "ohne nennenswerten Einfluss" line.
//     - localAgreement `"agree"` / `"single"`: one combined line in the
//       agreed direction.
//     - localAgreement `"disagree"`: one line per meaningful variant, each
//       prefixed by its variant subject (Mittelwert / Schwankung).
function variantSubject(kind: VariantKind): string {
    return kind === "std" ? "Schwankung" : "Mittelwert"
}

// Context the card threads in: latest instance's source date (YYYY-MM-DD)
// and the number of instances that went into the averaged contribution.
// Both come from the local-SHAP response — kept on a separate object so
// the helper's primary input (the signal DTO) stays clean.
export type PatientStatementContext = {
    latestInstanceDate: string | null
    instanceCount: number
}

// Format a date range bracket like " (vom 26.05. bis 01.06.)" for the given
// source date and lookback window.  Applies the rolling-feature shift: the
// window covers `[source_date − lookbackDays, source_date − 1]`, because the
// rolling features in the training pipeline exclude the source date itself
// to avoid target leakage.  Returns the empty string when the inputs aren't
// usable — caller appends unconditionally.
function buildDateRangeSuffix(
    latestInstanceDate: string | null,
    lookbackDays: number | null,
): string {
    if (!latestInstanceDate || !lookbackDays || lookbackDays <= 0) return ""
    // Parse the YYYY-MM-DD source date in UTC so locale doesn't shift the day.
    const sourceDate = new Date(`${latestInstanceDate}T00:00:00Z`)
    if (Number.isNaN(sourceDate.getTime())) return ""
    const toDate = new Date(sourceDate)
    toDate.setUTCDate(toDate.getUTCDate() - 1)
    const fromDate = new Date(sourceDate)
    fromDate.setUTCDate(fromDate.getUTCDate() - lookbackDays)
    const fmt = (d: Date) =>
        d.toLocaleDateString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            timeZone: "UTC",
        })
    return ` (vom ${fmt(fromDate)} bis ${fmt(toDate)})`
}

// Format the contribution prefix line.  Singular `1 Vorhersage` is a
// special case to avoid the ungrammatical "letzten 1 Vorhersagen".  Returns
// undefined when there are no instances — caller suppresses the prefix.
function buildContributionPrefix(instanceCount: number): string | undefined {
    if (instanceCount <= 0) return undefined
    if (instanceCount === 1) return "(im Mittel der letzten Vorhersage)"
    return `(im Mittel der letzten ${instanceCount} Vorhersagen)`
}

export function patientStatementsForSignal(
    signal: RankedSignalDto,
    ctx: PatientStatementContext,
): PatientStatement {
    const rawValuesLine =
        signal.variants
            .map((v: RankedVariantDto) => {
                const subject = variantSubject(v.kind)
                return `${subject} ${formatFeatureValue(signal.groupKey, v.currentValue)}`
            })
            .join(" · ") + buildDateRangeSuffix(ctx.latestInstanceDate, signal.lookbackDays)

    const contributionPrefix = buildContributionPrefix(ctx.instanceCount)

    const meaningful = signal.variants.filter((v) => v.direction !== "neutral")

    if (meaningful.length === 0) {
        return {
            rawValuesLine,
            contributionPrefix,
            contributionLines: [
                { text: "ohne nennenswerten Einfluss", type: "neutral" },
            ],
        }
    }

    if (signal.localAgreement === "agree" || signal.localAgreement === "single") {
        const first = meaningful[0]
        const positive = first.direction === "increases"
        return {
            rawValuesLine,
            contributionPrefix,
            contributionLines: [
                {
                    text: positive
                        ? "trägt zur höheren Vorhersage bei"
                        : "senkt die Vorhersage",
                    type: signalDirectionToStatementType(first.direction),
                },
            ],
        }
    }

    // localAgreement === "disagree" — one line per meaningful variant
    return {
        rawValuesLine,
        contributionPrefix,
        contributionLines: meaningful.map((v) => {
            const subject = variantSubject(v.kind)
            const positive = v.direction === "increases"
            return {
                text: positive
                    ? `${subject} trägt zur höheren Vorhersage bei`
                    : `${subject} senkt die Vorhersage`,
                type: signalDirectionToStatementType(v.direction),
            }
        }),
    }
}
